// The JSON API, written once for both the server and the phone.
//
// Mynah ships two ways. With a Node process under it there is an HTTP server, accounts and a
// SQLite file; on a phone there is a web view, one learner and IndexedDB. THE APP ON TOP IS THE
// SAME APP, and it must behave the same — the same card comes next, the same reset clears the same
// cards, the same sentence counts as readable. So the rules live here, above `Store`, and the two
// deployments differ only in what they hand in: a transport, a store, an environment, an account.
//
// Nothing in this file knows about HTTP. It takes a method, a path, a query and a parsed body, and
// answers with a status and a value. src/server.ts turns that into a response; web/local.ts
// returns it straight to the caller, with no socket in between.

import { composeSentences, composeStory } from './composer.js';
import { needsFor, oneWordAway, readableNow, shortestFirst } from './readable.js';
import { opens, planNext, type Step } from './plan.js';
import { writingFor } from './writing.js';
import {
  complete,
  DEFAULT_BASE_URL,
  DEFAULT_MODEL,
  keyHint,
  llmConfig,
  LlmError,
  type Env,
  type LlmConfig,
  type LlmSettings,
} from './llm.js';
import {
  deckProgress,
  resetProgress,
  unknownWordCounts,
  type Store,
} from './progress.js';
import { newCard, pickNext, review } from './srs.js';
import {
  cardKey,
  type CardMode,
  type Grade,
  type Hints,
  type ItemKind,
  type Pair,
  type Sentences,
  type Stories,
  type Strokes,
  type TestResult,
  type Tutor,
  type Vocab,
} from './types.js';

/** The generated data files. Read from disk by the server, fetched from the bundle on a phone. */
export interface Corpus {
  /** Which language this build teaches, and which it teaches it in. */
  pair: Pair;
  vocab: Vocab;
  sentences: Sentences;
  stories: Stories;
  tutor: Tutor;
  hints: Hints;
  /** Stroke order, or null for a writing system the build has none for. */
  strokes: Strokes | null;
}

export interface ApiContext {
  store: Store;
  corpus: Corpus;
  /**
   * The environment, for the model settings. A server passes `process.env`; a phone passes {},
   * because a phone has no operator and the key can only have come from the settings page.
   */
  env: Env;
  /** Who this is. On a phone, the one local learner. */
  account: { id: string; displayName: string };
  /**
   * Local means the person at the keyboard owns this copy: they may set the model key, and there
   * is nobody to sign out as. A hosted instance is the other case and both of those flip.
   */
  authMode: 'local' | 'oidc';
  /**
   * Which course is installed on this device, when this build can hold one of its own.
   *
   * ABSENT ON A SERVER, and that absence is the answer rather than a gap. A hosted instance
   * teaches what its operator generated it for; a phone carries its corpus in its own storage and
   * can be handed a different one. The app asks rather than checking which build it is, because
   * the moment the UI knows that there are two apps to keep working instead of one.
   *
   * Returns null when nothing is installed and the bundled corpus is in use.
   */
  installedCorpus?: () => Promise<string | null>;
  /**
   * The courses this instance already holds, and which one this account is in.
   *
   * NOT THE SAME QUESTION AS installedCorpus, though both are about "which course". That one is a
   * phone deciding what to DOWNLOAD into its own storage; this is a server that already has every
   * course on disk and is being asked which of them to teach this account. One transfers, the
   * other chooses, and the difference is roughly four megabytes and a progress bar.
   *
   * Absent where the build serves one course and cannot be told otherwise.
   */
  courses?: {
    available: () => { id: string; pair: Pair }[];
    chosen: () => string;
    /** False when the id names no installed course; nothing is written. */
    choose: (id: string) => boolean;
  };
  /** Injectable so tests can pin it. */
  now?: () => number;
}

export interface ApiRequest {
  method: string;
  /** The path, already stripped of its query string. */
  path: string;
  query: URLSearchParams;
  /** The parsed request body, or {} when there was none. */
  body: unknown;
}

export interface ApiResponse {
  status: number;
  body: unknown;
}

/** See the note on PREFS_KEY's history in the git log: one row holds the whole object. */
const prefsKey = (userId: string) => `prefs:${userId}`;
const introLimitKey = (userId: string) => `introLimit:${userId}`;

export const DEFAULT_INTRO_LIMIT = 15;

/**
 * Where the model settings live.
 *
 * NOT per user, and that is deliberate. A key is a bill somebody pays, and on a hosted instance the
 * person paying is whoever runs it — not whichever invited learner last opened Settings. So it is
 * one setting for the instance, and on a hosted instance it can only be READ: the environment is
 * the operator's channel and the browser is not. On a desktop or phone copy the person holding the
 * device is the operator, and the box is how they set it.
 */
const LLM_KEY_SETTING = 'llm:key';
const LLM_MODEL_SETTING = 'llm:model';
const LLM_BASE_SETTING = 'llm:base';

const GRADES: Grade[] = ['again', 'hard', 'good', 'easy'];
const MODES: CardMode[] = ['hanzi-en', 'en-hanzi', 'audio'];

function savedLlm(store: Store): LlmSettings {
  return {
    apiKey: store.getSetting(LLM_KEY_SETTING),
    model: store.getSetting(LLM_MODEL_SETTING),
    baseUrl: store.getSetting(LLM_BASE_SETTING),
  };
}

/** The model this copy will actually use, environment and settings resolved. */
function activeLlm(ctx: ApiContext): LlmConfig | null {
  return llmConfig(ctx.env, savedLlm(ctx.store));
}

function introLimit(ctx: ApiContext): number {
  const raw =
    ctx.store.getSetting(introLimitKey(ctx.account.id)) ?? ctx.store.getSetting('introLimit');
  const n = raw === null ? NaN : Number(raw);
  return Number.isFinite(n) && n >= 0 ? n : DEFAULT_INTRO_LIMIT;
}

/** Local midnight, so the daily new-word limit resets when the learner's day does. */
function startOfToday(now: number): number {
  const d = new Date(now);
  d.setHours(0, 0, 0, 0);
  return d.getTime();
}

/** Resolve a deck id to its kind and its ordered item ids, whichever kind of deck it is. */
/** The deck with no contents of its own: what to learn next, given what is known right now. */
export const NEXT_DECK = 'next-up';

export function resolveDeck(
  corpus: Corpus,
  deckId: string,
  /**
   * What the learner knows, for the one deck whose contents depend on it.
   *
   * Passed in rather than read here, because this function is the corpus's answer to "what is in
   * this deck" and the corpus does not know who is asking. Omitting it simply means the computed
   * deck does not resolve, which is the right answer for a caller that has no learner in hand.
   */
  plan?: () => number[],
): { kind: ItemKind; items: number[] } | null {
  if (deckId === NEXT_DECK) return plan ? { kind: 'word', items: plan() } : null;
  const word = corpus.vocab.decks.find((d) => d.id === deckId);
  if (word) return { kind: 'word', items: word.words };
  const sentence = corpus.sentences.decks.find((d) => d.id === deckId);
  if (sentence) return { kind: 'sentence', items: sentence.sentences };
  return null;
}

/**
 * How many words the computed deck holds.
 *
 * A deck, not a syllabus: enough that finishing it is a real session and few enough that it is
 * still recognisably "what to do next". It re-plans from scratch as soon as any of them is learned,
 * so the number is a window rather than a commitment.
 */
const PLAN_SIZE = 30;

/** What to learn next for the learner this request belongs to. */
function planFor(ctx: ApiContext): Step[] {
  const { vocab, sentences, pair } = ctx.corpus;
  const known = new Set(ctx.store.knownItems('word').map((k) => k.id));
  return planNext(
    needsFor(sentences.sentences, vocab.words),
    known,
    vocab.words,
    PLAN_SIZE,
    writingFor(pair).script,
  );
}

const ok = (body: unknown): ApiResponse => ({ status: 200, body });
const bad = (status: number, error: string): ApiResponse => ({ status, body: { error } });

/**
 * Answer one API call.
 *
 * Returns 404 for a path this does not serve, the same as the server always did — the caller does
 * not have to know the route table to know it missed.
 */
export async function handleApi(ctx: ApiContext, req: ApiRequest): Promise<ApiResponse> {
  const { store, corpus } = ctx;
  const { vocab, sentences, pair } = corpus;
  const now = (ctx.now ?? Date.now)();
  const me = ctx.account.id;
  const { method, path, query } = req;

  if (method === 'GET' && path === '/api/pair') return ok(corpus.pair);
  // Can this copy be handed a different course, and has it been? Answered here rather than by the
  // app looking at its own build, so there is one app rather than two.
  if (method === 'GET' && path === '/api/corpus') {
    return ok({
      installable: ctx.installedCorpus !== undefined,
      installed: ctx.installedCorpus ? await ctx.installedCorpus() : null,
    });
  }
  if (method === 'GET' && path === '/api/vocab') return ok(vocab);
  if (method === 'GET' && path === '/api/sentences') return ok(sentences);
  if (method === 'GET' && path === '/api/stories') return ok(corpus.stories);
  if (method === 'GET' && path === '/api/tutor') return ok(corpus.tutor);
  if (method === 'GET' && path === '/api/hints') return ok(corpus.hints);
  if (method === 'GET' && path === '/api/strokes') return ok(corpus.strokes);

  /**
   * Which courses this instance can teach, and which one is mine.
   *
   * Separate from /api/corpus, which answers what a PHONE has downloaded. A server holds every
   * course it was built with, so this is a list and a choice rather than a transfer — no size, no
   * progress, nothing to resume.
   */
  if (method === 'GET' && path === '/api/courses') {
    if (!ctx.courses) return ok({ choosable: false, chosen: null, courses: [] });
    return ok({
      choosable: true,
      chosen: ctx.courses.chosen(),
      courses: ctx.courses.available(),
    });
  }

  /**
   * Study a different course.
   *
   * NOTHING IS CLEARED, and that is the whole reason this is a one-line write. Progress is keyed
   * by (user, pair, kind, item), so the Mandarin left behind is still there, still due when it was
   * due, and coming back lands on exactly the schedule that was left — see item-key.test.ts. A
   * course swap that had to move or delete anything would be a migration; this is a preference.
   */
  if (method === 'PUT' && path === '/api/courses') {
    if (!ctx.courses) return { status: 400, body: { error: 'this build teaches one course' } };
    const wanted = (req.body as { id?: unknown }).id;
    if (typeof wanted !== 'string') {
      return { status: 400, body: { error: 'which course? send { id }' } };
    }
    if (!ctx.courses.choose(wanted)) {
      return { status: 400, body: { error: `no course "${wanted}" is installed here` } };
    }
    return ok({ chosen: ctx.courses.chosen() });
  }

  if (method === 'GET' && path === '/api/progress') {
    const states = store.states();
    return ok({
      states: Object.fromEntries(states),
      decks: deckProgress(states, store.knownKeys(), vocab, sentences, now),
      introducedToday: store.introducedSince(startOfToday(now)),
      introLimit: introLimit(ctx),
      history: store.reviewHistory(30, now),
    });
  }

  // The next card for a deck. SELECTION LIVES HERE so the browser cannot drift from the
  // scheduler's idea of what is due — on a phone the browser is also the server, and the rule
  // still holds: the UI asks, it does not decide.
  if (method === 'GET' && path === '/api/next') {
    const deckId = query.get('deck') ?? '';
    const deck = resolveDeck(corpus, deckId, () => planFor(ctx).map((step) => step.id));
    if (!deck) return bad(400, `unknown deck: ${deckId}`);

    // Cards the learner skipped in this session. Held by the client because skipping is a
    // session-scoped "not now" and deliberately leaves no trace in the schedule.
    // Drop empty segments BEFORE converting: Number('') is 0, so a missing or empty parameter
    // would otherwise exclude word 0 — which is 的, the single most common word in every deck.
    const exclude = new Set(
      (query.get('exclude') ?? '')
        .split(',')
        .filter((part) => part.trim() !== '')
        .map(Number)
        .filter((n) => Number.isInteger(n) && n >= 0)
        .map((n) => cardKey(deck.kind, n)),
    );
    // Anything declared already known is off the table entirely — not pushed years out, not shown
    // once more to be sure. The learner said they have it; continuing to offer it is the app not
    // listening.
    //
    // Unless they have asked for them back, which is how the toggle on a card is undone from where
    // they are standing rather than from a settings page two screens away.
    if (query.get('showKnown') !== '1') {
      for (const key of store.knownKeys()) exclude.add(key);
    }

    // "Sentences I can almost read": restrict which NEW sentences may be introduced to those with
    // at most this many words the learner has not learned. Recomputed per request, because the
    // answer changes every time a card is graded.
    const maxUnknownRaw = query.get('maxUnknown');
    let introducible: Set<string> | undefined;
    if (deck.kind === 'sentence' && maxUnknownRaw !== null) {
      const maxUnknown = Number(maxUnknownRaw);
      if (!Number.isInteger(maxUnknown) || maxUnknown < 0) {
        return bad(400, 'maxUnknown must be a non-negative integer');
      }
      const counts = unknownWordCounts(store.states(), store.knownKeys(), vocab, sentences);
      introducible = new Set(
        deck.items
          .filter((n) => (counts.get(n) ?? Infinity) <= maxUnknown)
          .map((n) => cardKey('sentence', n)),
      );
    }

    const states = store.states();
    // Selection runs over namespaced keys, so a word and a sentence sharing an index cannot be
    // mistaken for the same card.
    const key = pickNext(
      deck.items.map((n) => cardKey(deck.kind, n)),
      states,
      {
        now,
        introduced: store.introducedSince(startOfToday(now)),
        introLimit: introLimit(ctx),
        exclude,
        shuffle: query.get('shuffle') === '1',
        introducible,
      },
    );
    if (key === null) return ok({ id: null, kind: deck.kind, reason: 'done' });
    const id = Number(key.slice(key.indexOf(':') + 1));
    return ok({ id, kind: deck.kind, state: states.get(key) ?? newCard(now) });
  }

  if (method === 'POST' && path === '/api/review') {
    const body = req.body as {
      id?: number;
      kind?: ItemKind;
      deckId?: string;
      mode?: CardMode;
      grade?: Grade;
    };
    const { id, deckId, mode, grade } = body;
    const kind: ItemKind = body.kind === 'sentence' ? 'sentence' : 'word';
    const exists =
      kind === 'sentence' ? sentences.sentences[id as number] : vocab.words[id as number];
    if (typeof id !== 'number' || !exists) return bad(400, `id must be a valid ${kind} index`);
    if (!deckId || !resolveDeck(corpus, deckId)) return bad(400, 'deckId must name a real deck');
    if (!mode || !MODES.includes(mode)) return bad(400, 'bad mode');
    if (!grade || !GRADES.includes(grade)) return bad(400, 'bad grade');

    const prior = store.states().get(cardKey(kind, id)) ?? newCard(now);
    const next = review(prior, grade, now);
    store.putState(kind, id, next, now);
    store.logReview({ kind, id, deckId, mode, grade }, now);
    return ok({ id, kind, state: next });
  }

  // Everything the learner has said they already know, with enough about each to list it.
  if (method === 'GET' && path === '/api/known') {
    const kind: ItemKind = query.get('kind') === 'sentence' ? 'sentence' : 'word';
    return ok({
      kind,
      // The total is what the count is measured against, and the client would otherwise have to
      // load the whole vocabulary to work it out.
      total: kind === 'word' ? vocab.words.length : sentences.sentences.length,
      items: store.knownItems(kind),
    });
  }

  // Declare items known, or take it back. Takes a LIST, because the useful case is sweeping a
  // page of words at once — one at a time is the rare one.
  if (method === 'POST' && path === '/api/known') {
    const body = req.body as { kind?: string; ids?: unknown; known?: boolean };
    const kind: ItemKind = body.kind === 'sentence' ? 'sentence' : 'word';
    if (!Array.isArray(body.ids)) return bad(400, 'ids must be an array');
    const limit = kind === 'word' ? vocab.words.length : sentences.sentences.length;
    const ids = body.ids.filter(
      (n): n is number => Number.isInteger(n) && (n as number) >= 0 && (n as number) < limit,
    );
    if (ids.length !== body.ids.length) return bad(400, 'every id must be an item that exists');
    const changed = store.setKnown(kind, ids, body.known !== false, now);
    return ok({ changed, known: store.knownItems(kind).length });
  }

  // What to learn next, and why. Recomputed on every request, because the answer is a function of
  // what the learner knows and that changes under it — a stored plan is a stale plan.
  if (method === 'GET' && path === '/api/plan') {
    const plan = planFor(ctx);
    return ok({
      known: store.knownItems('word').length,
      size: plan.length,
      opens: opens(plan),
      items: plan.map((step) => {
        const w = vocab.words[step.id]!;
        return {
          id: step.id,
          s: w.s,
          p: w.p,
          e: w.e.slice(0, 2),
          pos: w.pos,
          completes: step.completes,
          approaches: step.approaches,
        };
      }),
    });
  }

  // Sentences from the corpus this learner can already read every word of. No model, no key, no
  // network -- the same promise "Made for you" makes, kept out of what is already on the disk.
  if (method === 'GET' && path === '/api/readable') {
    const known = new Set(store.knownItems('word').map((k) => k.id));
    const needs = needsFor(sentences.sentences, vocab.words);
    const found = shortestFirst(readableNow(needs, known), sentences.sentences);
    // Paged, because this is thousands of sentences at a few hundred known words and nobody reads
    // a page of seven thousand. The COUNT is the headline and is always exact; the page is a page.
    const offset = Math.max(0, Number(query.get('offset') ?? 0) || 0);
    const limit = Math.min(60, Math.max(1, Number(query.get('limit') ?? 30) || 30));
    const page = found.slice(offset, offset + limit);
    return ok({
      known: known.size,
      corpus: sentences.sentences.length,
      readable: found.length,
      offset,
      items: page.map((at) => ({ at, ...sentences.sentences[at]! })),
      more: offset + page.length < found.length,
      // What one more word would buy, which is the only useful thing to say to somebody who has
      // just been told the number is small.
      next: oneWordAway(needs, known).map(({ id, unlocks }) => {
        const w = vocab.words[id]!;
        return { id, s: w.s, p: w.p, e: w.e[0] ?? '', unlocks };
      }),
    });
  }

  // Sentences written for this learner, and the machinery for making more.
  if (method === 'GET' && path === '/api/made') {
    return ok({
      // The client needs to know whether to offer the button at all, and saying so plainly beats
      // letting it find out by getting an error.
      available: activeLlm(ctx) !== null,
      known: store.knownItems('word').length,
      items: store.madeSentences(),
    });
  }

  if (method === 'POST' && path === '/api/made') {
    const config = activeLlm(ctx);
    if (!config) {
      return bad(501, 'No model is configured, so Mynah cannot write sentences on this machine.');
    }
    const body = req.body as { theme?: string };
    const known = store.knownItems('word').map((k) => k.id);
    if (known.length < 8) {
      return bad(400, 'Mark a few more words as known first — there is not enough here to write with.');
    }
    try {
      const result = await composeSentences(config, { pair, words: vocab.words, sentences: sentences.sentences }, known, {
        theme: typeof body.theme === 'string' ? body.theme.slice(0, 120) : undefined,
      });
      store.addMadeSentences(result.kept, now);
      return ok({
        // The SAME shape the GET returns, plus what just happened. Returning a partial one meant a
        // successful write repainted the page as "no model is configured", because the field
        // saying otherwise was missing rather than false.
        available: true,
        known: known.length,
        items: store.madeSentences(),
        // The rejects go back too. A pass rate that quietly drops is the failure mode of this whole
        // feature, and it is invisible unless somebody is told.
        made: result.kept.length,
        asked: result.asked,
        rejected: result.rejected,
        usedVocabulary: result.usedVocabulary,
      });
    } catch (err) {
      const kind = err instanceof LlmError ? err.kind : 'unusable';
      return bad(kind === 'unreachable' ? 502 : 500, err instanceof Error ? err.message : String(err));
    }
  }

  if (method === 'DELETE' && path.startsWith('/api/made/')) {
    const id = Number(path.slice('/api/made/'.length));
    if (!Number.isInteger(id)) return bad(400, 'not a sentence id');
    return ok({ deleted: store.deleteMadeSentence(id) });
  }

  // Stories written for this learner, in the same shape as the handwritten ones.
  if (method === 'GET' && path === '/api/made-stories') {
    return ok({
      available: activeLlm(ctx) !== null,
      known: store.knownItems('word').length,
      items: store.madeStories(),
    });
  }

  if (method === 'POST' && path === '/api/made-stories') {
    const config = activeLlm(ctx);
    if (!config) return bad(501, 'No model is configured, so Mynah cannot write stories here.');
    const body = req.body as { theme?: string };
    const known = store.knownItems('word').map((k) => k.id);
    // A story needs something to happen in it, which needs more words than a few sentences do.
    if (known.length < 25) {
      return bad(400, `A story needs more words than that. Mark ${25 - known.length} more as known.`);
    }
    try {
      const result = await composeStory(config, { pair, words: vocab.words, sentences: sentences.sentences }, known, {
        theme: typeof body.theme === 'string' ? body.theme.slice(0, 120) : undefined,
      });
      if (!result.story) {
        // Said plainly rather than dressed up. The vocabulary is too small, or too awkward, to
        // hold a story together — and pretending otherwise means showing one that does not.
        return ok({
          available: true,
          known: known.length,
          items: store.madeStories(),
          made: false,
          attempts: result.attempts,
          rejected: result.rejected,
        });
      }
      store.addMadeStory(result.story, now);
      return ok({
        available: true,
        known: known.length,
        items: store.madeStories(),
        made: true,
        attempts: result.attempts,
        rejected: result.rejected,
      });
    } catch (err) {
      const kind = err instanceof LlmError ? err.kind : 'unusable';
      return bad(kind === 'unreachable' ? 502 : 500, err instanceof Error ? err.message : String(err));
    }
  }

  if (method === 'DELETE' && path.startsWith('/api/made-stories/')) {
    const id = Number(path.slice('/api/made-stories/'.length));
    if (!Number.isInteger(id)) return bad(400, 'not a story id');
    return ok({ deleted: store.deleteMadeStory(id) });
  }

  if (method === 'GET' && path === '/api/tests') {
    return ok({ tests: store.testHistory() });
  }

  if (method === 'POST' && path === '/api/test') {
    const body = req.body as Partial<TestResult>;
    const { known, asked, right, bands } = body;
    // The client does the asking and the arithmetic — the vocabulary is already in the browser and
    // a round trip per question would make the test crawl. So the numbers are checked here rather
    // than trusted: a stored result is a measurement, and one that cannot be true is worse than
    // none at all.
    const whole = (n: unknown): n is number => typeof n === 'number' && Number.isInteger(n) && n >= 0;
    if (!whole(known) || !whole(asked) || !whole(right) || !Array.isArray(bands)) {
      return bad(400, 'known, asked and right must be non-negative integers, with bands');
    }
    if (right > asked) return bad(400, 'right cannot exceed asked');
    if (asked === 0) return bad(400, 'an unanswered test is not a result');
    const bandsOk = bands.every(
      (b) => whole(b?.from) && whole(b?.to) && whole(b?.asked) && whole(b?.right) && b.right <= b.asked,
    );
    if (!bandsOk) return bad(400, 'each band needs from, to, asked and right');
    const total = bands.reduce((n, b) => n + b.asked, 0);
    if (total !== asked) return bad(400, 'band counts must add up to asked');
    return ok(store.recordTest({ known, asked, right, bands }, now));
  }

  // Who the app thinks you are. The client uses this to decide whether to show a sign-out at all.
  if (method === 'GET' && path === '/api/me') {
    return ok({
      id: ctx.account.id,
      displayName: ctx.account.displayName,
      mode: ctx.authMode,
      // Local mode has nobody to sign out AS, so the UI must not offer it.
      canSignOut: ctx.authMode !== 'local',
    });
  }

  if (method === 'POST' && path === '/api/reset') {
    const body = req.body as { deckId?: string };
    return ok({ cleared: resetProgress(store, vocab, sentences, body.deckId) });
  }

  // The learner's UI preferences — theme, card style, which toggles are on.
  //
  // These lived in localStorage, which is keyed by ORIGIN, and the desktop shell asks the OS for a
  // free port on every launch. So every launch was a different origin with its own empty storage,
  // and every preference reset: the theme most visibly, but also the card style, the pinyin toggle,
  // shuffle and the chosen voice. Keeping them next to the progress they belong with fixes that,
  // and makes the web and desktop apps agree about the same machine's settings.
  if (method === 'GET' && path === '/api/prefs') {
    const raw = store.getSetting(prefsKey(me));
    return ok(raw ? (JSON.parse(raw) as unknown) : {});
  }

  if (method === 'PUT' && path === '/api/prefs') {
    const body = req.body;
    if (typeof body !== 'object' || body === null || Array.isArray(body)) {
      return bad(400, 'preferences must be an object');
    }
    const payload = JSON.stringify(body);
    // A bound, because nothing else limits what a client may put here.
    if (payload.length > 8192) return bad(400, 'preferences too large');
    store.setSetting(prefsKey(me), payload);
    return ok(body);
  }

  // What the model settings are, WITHOUT the key. See keyHint.
  if (method === 'GET' && path === '/api/model') {
    const saved = savedLlm(store);
    const active = activeLlm(ctx);
    return ok({
      configured: active !== null,
      // Where the key came from, because "it works but the box is empty" is otherwise a mystery.
      source: saved.apiKey ? 'settings' : active ? 'environment' : null,
      hint: keyHint(active?.apiKey),
      model: active?.model ?? DEFAULT_MODEL,
      baseUrl: active?.baseUrl ?? DEFAULT_BASE_URL,
      // A hosted instance takes its key from the operator's environment, not from whichever
      // invited learner last opened Settings.
      editable: ctx.authMode === 'local',
      defaults: { model: DEFAULT_MODEL, baseUrl: DEFAULT_BASE_URL },
    });
  }

  if (method === 'PUT' && path === '/api/model') {
    if (ctx.authMode !== 'local') {
      return bad(403, 'On a hosted instance the model is configured by whoever runs the server.');
    }
    const body = req.body as { apiKey?: string | null; model?: string; baseUrl?: string };
    // An empty string clears; an absent field leaves the setting alone. Without that distinction
    // there is no way to remove a key you pasted by mistake.
    if (body.apiKey !== undefined) store.setSetting(LLM_KEY_SETTING, (body.apiKey ?? '').trim());
    if (body.model !== undefined) store.setSetting(LLM_MODEL_SETTING, body.model.trim());
    if (body.baseUrl !== undefined) store.setSetting(LLM_BASE_SETTING, body.baseUrl.trim());
    const active = activeLlm(ctx);
    return ok({
      configured: active !== null,
      hint: keyHint(active?.apiKey),
      model: active?.model ?? DEFAULT_MODEL,
      baseUrl: active?.baseUrl ?? DEFAULT_BASE_URL,
    });
  }

  // Does the key actually work? Asked on demand, because a key that is wrong should say so here
  // rather than the first time somebody asks for sentences.
  if (method === 'POST' && path === '/api/model/check') {
    const config = activeLlm(ctx);
    if (!config) return bad(400, 'No key is set.');
    try {
      const answer = await complete(config, [{ role: 'user', content: 'Reply with the word: ok' }], {
        maxTokens: 8,
        temperature: 0,
      });
      return ok({ ok: true, model: config.model, said: answer.trim().slice(0, 40) });
    } catch (err) {
      return ok({ ok: false, error: err instanceof Error ? err.message : String(err) });
    }
  }

  if (method === 'PUT' && path === '/api/settings') {
    const body = req.body as { introLimit?: number };
    if (body.introLimit !== undefined) {
      const n = Number(body.introLimit);
      if (!Number.isFinite(n) || n < 0 || n > 500) return bad(400, 'introLimit must be between 0 and 500');
      store.setSetting(introLimitKey(me), String(Math.round(n)));
    }
    return ok({ introLimit: introLimit(ctx) });
  }

  return bad(404, 'no such endpoint');
}
